import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService, userService } from '../../services';
import { logger } from '@/utils/logger';
import { translate_error } from '../../utils/translate_error';

// Async thunks
export const signUpUser = createAsyncThunk(
  'auth/signUpUser',
  async ({ email, password, full_name }, { rejectWithValue }) => {
    try {
      const { data, error } = await authService.signUp(email, password, { full_name });
      if (error) throw new Error(error);

      // Enrich with profile data if user was created (trigger inserts into public.users)
      if (data?.user) {
        const { data: profile_data } = await userService.getUserProfile(data.user.id);

        if (profile_data) {
          return {
            ...data,
            user: {
              ...data.user,
              ...profile_data,
              profile: profile_data
            }
          };
        }
      }

      return data;
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

/**
 * signInUser - Authenticates user with email/password and fetches profile
 *
 * Flow:
 * 1. User submits login → signInUser() authenticates with Supabase
 * 2. Fetches full profile from public.users (avatar, is_artist, etc.)
 * 3. Returns enriched user data to Redux immediately
 *
 * Note: onAuthStateChange SIGNED_IN handler is intentionally a no-op
 * (calling Supabase there causes client hang). Profile enrichment
 * happens here instead.
 */
export const signInUser = createAsyncThunk(
  'auth/signInUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data, error } = await authService.signIn(email, password);
      if (error) throw new Error(error);

      // Enrich with profile data so UI updates immediately (avatar, is_artist, etc.)
      if (data?.user) {
        const { data: profile_data } = await userService.getUserProfile(data.user.id);

        if (profile_data) {
          return {
            ...data,
            user: {
              ...data.user,
              ...profile_data,
              profile: profile_data
            }
          };
        }
      }

      return data;
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

// Initiates Google OAuth redirect — no user data returned here.
// After redirect back, AuthCallback page + getCurrentUser() handle session loading.
export const signInWithGoogle = createAsyncThunk(
  'auth/signInWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      const { data, error } = await authService.signInWithGoogle();
      if (error) throw new Error(error);
      return data; // null — browser is redirecting
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

export const signOutUser = createAsyncThunk(
  'auth/signOutUser',
  async (_, { rejectWithValue }) => {
    try {
      const { error } = await authService.signOut();
      if (error) throw new Error(error);
      return true;
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

/**
 * getCurrentUser - FULL initialization with user profile
 *
 * CRITICAL FIX: This now handles EVERYTHING including getUserProfile
 * to avoid calling Supabase client during onAuthStateChange (causes hang)
 *
 * Flow:
 * 1. App mounts → getCurrentUser() checks for session
 * 2. If session exists → fetch user profile WITH DELAY
 * 3. onAuthStateChange → ONLY updates if user already set (prevents hang)
 *
 * The 1 second delay is CRITICAL - Supabase client hangs if called too early after reload
 */
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const { session, error } = await authService.getCurrentSession();
      if (error) throw new Error(error);

      if (session?.user) {
        // CRITICAL: Wait for Supabase client to fully initialize
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Now safe to call getUserProfile
        const { data: profile_data, error: profileError } = await userService.getUserProfile(session.user.id);

        if (profileError) {
          // Fallback to basic user if profile fetch fails
          return { user: session.user, session };
        }

        return {
          user: {
            ...session.user,
            ...profile_data,
            profile: profile_data
          },
          session
        };
      }

      return null;
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async ({ user_id, updates }, { rejectWithValue }) => {
    try {
      const { data, error } = await userService.updateUserProfile(user_id, updates);
      if (error) throw new Error(error);
      return data;
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

/**
 * deleteAccount - Permanently deletes the user's account
 *
 * Calls backend DELETE /api/users/me which:
 * 1. Checks for active orders (rejects if any)
 * 2. Anonymizes user data in public.users
 * 3. Archives artworks, deletes favorites/comments/etc.
 * 4. Deletes auth user (prevents re-login)
 *
 * After backend succeeds, signs out locally and clears Redux state.
 */
export const deleteAccount = createAsyncThunk(
  'auth/deleteAccount',
  async (_, { rejectWithValue }) => {
    try {
      const { data, error } = await userService.deleteAccount();
      if (error) throw new Error(error);

      // Sign out locally after backend deletion succeeds
      await authService.signOut();

      return data;
    } catch (error) {
      return rejectWithValue(translate_error(error.message));
    }
  }
);

const initialState = {
  user: null,
  session: null,
  is_authenticated: false,
  loading: false,
  error: null,
  initialized: false
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
      state.error = null;
    },
    setError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.is_authenticated = !!action.payload;
      state.loading = false;
      state.error = null;
    },
    clearUser: (state) => {
      state.user = null;
      state.session = null;
      state.is_authenticated = false;
      state.loading = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setInitialized: (state) => {
      state.initialized = true;
    }
  },
  extraReducers: (builder) => {
    builder
      // Sign up
      .addCase(signUpUser.pending, (state) => {
        logger.debug('AuthSlice', 'signUpUser pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        logger.info('AuthSlice', 'signUpUser fulfilled', {
          user_id: action.payload.user?.id,
          email: action.payload.user?.email,
        });
        state.loading = false;
        if (action.payload.user) {
          state.user = action.payload.user;
          state.session = action.payload.session;
          state.is_authenticated = true;
        }
      })
      .addCase(signUpUser.rejected, (state, action) => {
        logger.error('AuthSlice', 'signUpUser rejected', { error: action.payload });
        state.loading = false;
        state.error = action.payload;
      })
      
      // Sign in
      .addCase(signInUser.pending, (state) => {
        logger.debug('AuthSlice', 'signInUser pending');
        state.loading = true;
        state.error = null;
      })
      .addCase(signInUser.fulfilled, (state, action) => {
        logger.info('AuthSlice', 'signInUser fulfilled', {
          user_id: action.payload.user?.id,
          email: action.payload.user?.email,
        });
        state.loading = false;
        if (action.payload.user) {
          state.user = action.payload.user;
          state.session = action.payload.session;
          state.is_authenticated = true;
        }
      })
      .addCase(signInUser.rejected, (state, action) => {
        logger.error('AuthSlice', 'signInUser rejected', { error: action.payload });
        state.loading = false;
        state.error = action.payload;
      })
      
      // Sign in with Google
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        // Google OAuth will redirect, so we don't set user here
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Sign out
      .addCase(signOutUser.pending, (state) => {
        logger.debug('AuthSlice', 'signOutUser pending');
        state.loading = true;
      })
      .addCase(signOutUser.fulfilled, (state) => {
        logger.info('AuthSlice', 'signOutUser fulfilled');
        state.loading = false;
        state.user = null;
        state.session = null;
        state.is_authenticated = false;
        state.error = null;
      })
      .addCase(signOutUser.rejected, (state, action) => {
        logger.error('AuthSlice', 'signOutUser rejected', { error: action.payload });
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        logger.debug('AuthSlice', 'getCurrentUser pending');
        // Always set loading to prevent ProtectedRoute from redirecting
        // before onAuthStateChange completes
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        logger.info('AuthSlice', 'getCurrentUser fulfilled', {
          has_user: !!action.payload?.user,
          user_id: action.payload?.user?.id,
        });
        state.loading = false;
        state.initialized = true;
        if (action.payload?.user) {
          state.user = action.payload.user;
          state.session = action.payload.session;
          state.is_authenticated = true;
        } else {
          state.user = null;
          state.session = null;
          state.is_authenticated = false;
        }
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        logger.warn('AuthSlice', 'getCurrentUser rejected', { error: action.payload });
        state.loading = false;
        state.initialized = true;
        state.user = null;
        state.session = null;
        state.is_authenticated = false;
        state.error = action.payload;
      })
      
      // Update user profile
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        if (state.user) {
          // Update both profile and spread data to user object
          Object.assign(state.user, action.payload);
          state.user.profile = action.payload;
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete account
      .addCase(deleteAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        logger.info('AuthSlice', 'deleteAccount fulfilled');
        state.loading = false;
        state.user = null;
        state.session = null;
        state.is_authenticated = false;
        state.error = null;
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        logger.error('AuthSlice', 'deleteAccount rejected', { error: action.payload });
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setLoading, setError, setUser, clearUser, clearError, setInitialized } = authSlice.actions;
export default authSlice.reducer;